/**
 * InlineProductSearch — click-to-edit picker that links a Product Dev record
 * to a Shopify-synced catalog product (products table, Class C per-org).
 *
 * Display mode mirrors InlineSelect. Edit mode opens a searchable command
 * palette backed by productDev.searchProducts (server-side ilike, 20 rows) —
 * the catalog is thousands of products, so a plain Select is unusable and
 * options must be fetched per keystroke rather than up front.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

interface InlineProductSearchProps {
  /** products.id currently linked (graduated_product_id), or null. */
  value: string | null;
  /** Resolved display title for the linked product (from the DTO join). */
  valueTitle: string | null;
  onSave: (next: string | null) => Promise<void> | void;
  placeholder?: string;
  readOnly?: boolean;
}

/** Debounce keystrokes so we don't fire a query per character. */
const useDebounced = (value: string, delayMs = 250): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
};

export function InlineProductSearch({
  value,
  valueTitle,
  onSave,
  placeholder,
  readOnly,
}: InlineProductSearchProps) {
  const { t } = useTranslation('productDev');
  const placeholderText = placeholder ?? t('productSearch.placeholder');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState('');
  const search = useDebounced(term);

  const optionsQuery = trpc.productDev.searchProducts.useQuery(
    { search: search || undefined },
    { enabled: editing, placeholderData: (prev) => prev },
  );
  const options = optionsQuery.data ?? [];

  const commit = async (next: string | null) => {
    if (next === (value ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      toast({
        title: t('productSearch.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('productSearch.toast.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={editing}
      onOpenChange={(open) => {
        if (readOnly || saving) return;
        setEditing(open);
        if (open) setTerm('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={readOnly}
          className={cn(
            'group inline-flex items-center gap-1 text-left w-full min-w-0 rounded px-1.5 py-1 -mx-1.5 transition-colors',
            !readOnly && 'hover:bg-muted/60 cursor-pointer',
            readOnly && 'cursor-default',
          )}
        >
          <span className="flex-1 min-w-0 truncate">
            {value ? (
              <Link
                to={`/products/${value}`}
                onClick={(e) => e.stopPropagation()}
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {valueTitle ?? t('productSearch.viewProduct')}
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
              </Link>
            ) : (
              <span className="text-muted-foreground italic">{placeholderText}</span>
            )}
          </span>
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />
          ) : (
            !readOnly && (
              <ChevronDown className="h-3 w-3 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            )
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        {/* Server-side search — cmdk's own filtering stays off. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('productSearch.search')}
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            <CommandEmpty>
              {optionsQuery.isLoading ? t('productSearch.searching') : t('productSearch.noResults')}
            </CommandEmpty>
            {value && (
              <CommandItem
                value="__none__"
                onSelect={() => void commit(null)}
                className="text-muted-foreground italic"
              >
                {t('productSearch.unlink')}
              </CommandItem>
            )}
            {options.map((opt) => (
              <CommandItem
                key={opt.id}
                value={opt.id}
                onSelect={() => void commit(opt.id)}
                className="gap-2"
              >
                {opt.image_url ? (
                  <img
                    src={opt.image_url}
                    alt=""
                    className="h-5 w-5 rounded object-cover shrink-0"
                  />
                ) : (
                  <span className="h-5 w-5 rounded bg-muted shrink-0" aria-hidden="true" />
                )}
                <span className="flex-1 min-w-0 truncate">{opt.title ?? opt.id}</span>
                {opt.status && opt.status !== 'active' && (
                  <span className="text-[10px] uppercase text-muted-foreground shrink-0">
                    {opt.status}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
